import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Person as PersonIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Today as TodayIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import {
  fetchDailySubstitutes,
  createDailySubstitute,
  updateDailySubstitute,
  deleteDailySubstitute,
} from '../slices/substituteSlice';
import { fetchStaffMinimal } from '../slices/staffSlice';
import { fetchTeacherAbsences } from '../slices/staffSlice';
import { useToast } from '../context/ToastContext';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);
const FONT = 'Cairo, Tajawal, sans-serif';

const DAYS_OF_WEEK = [
  { value: 'sunday', label: 'الأحد' },
  { value: 'monday', label: 'الاثنين' },
  { value: 'tuesday', label: 'الثلاثاء' },
  { value: 'wednesday', label: 'الأربعاء' },
  { value: 'thursday', label: 'الخميس' },
  { value: 'friday', label: 'الجمعة' },
  { value: 'saturday', label: 'السبت' },
];

const SubstituteTeachersPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  // Redux state
  const { substitutes, loading } = useSelector((state) => state.substitute);
  const staffMinimal = useSelector((state) => state.staff.minimal);
  const { teacherAbsences } = useSelector((state) => state.staff);

  // Local state
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [draggedTeacher, setDraggedTeacher] = useState(null);

  // Get date for a specific day of week in the selected week
  const getDateForDay = useCallback((dayOfWeek, weekStart) => {
    const dayIndex = DAYS_OF_WEEK.findIndex(d => d.value === dayOfWeek);
    const weekStartDate = new Date(weekStart);
    weekStartDate.setHours(0, 0, 0, 0);
    const dayOfWeekIndex = weekStartDate.getDay(); // 0 = Sunday
    const targetDayIndex = dayIndex; // 0 = Sunday
    const diff = targetDayIndex - dayOfWeekIndex;
    const targetDate = new Date(weekStartDate);
    targetDate.setDate(weekStartDate.getDate() + diff);
    return targetDate;
  }, []);

  // Filter teachers only
  const teachersList = useMemo(() => {
    return staffMinimal.filter(s => s.is_teacher && !s.is_archived);
  }, [staffMinimal]);

  // Get absent teacher IDs for the selected week
  const absentTeacherIdsByDay = useMemo(() => {
    const result = {};
    DAYS_OF_WEEK.forEach(day => {
      const dayDate = getDateForDay(day.value, selectedWeek);
      const dateStr = format(dayDate, 'yyyy-MM-dd');
      const absentIds = teacherAbsences
        .filter(a => {
          if (!a.absence_date) return false;
          const absenceDate = format(new Date(a.absence_date), 'yyyy-MM-dd');
          return absenceDate === dateStr;
        })
        .map(a => {
          // Handle both teacher (object) and teacher (ID) formats
          const teacherId = a.teacher?.id || a.teacher;
          return String(teacherId);
        });
      result[day.value] = absentIds;
    });
    return result;
  }, [teacherAbsences, selectedWeek, getDateForDay]);

  // Fetch data
  useEffect(() => {
    dispatch(fetchDailySubstitutes());
    dispatch(fetchStaffMinimal({ employee_type: 'teacher', is_archived: false }));
    
    // Fetch absences for the week
    const weekDates = DAYS_OF_WEEK.map(day => format(getDateForDay(day.value, selectedWeek), 'yyyy-MM-dd'));
    weekDates.forEach(dateStr => {
      dispatch(fetchTeacherAbsences({ absence_date: dateStr }));
    });
  }, [dispatch, selectedWeek, getDateForDay]);

  // Get substitutes for a specific day
  const getSubstitutesForDay = useCallback((dayOfWeek) => {
    const dayDate = getDateForDay(dayOfWeek, selectedWeek);
    const dateStr = format(dayDate, 'yyyy-MM-dd');
    
    // Find substitute for this day and date
    return substitutes.find(sub => 
      sub.day_of_week === dayOfWeek && 
      sub.date === dateStr
    ) || substitutes.find(sub => 
      sub.day_of_week === dayOfWeek && 
      !sub.date // Weekly schedule
    );
  }, [substitutes, selectedWeek, getDateForDay]);

  // Handle drag start
  const handleDragStart = (e, teacher) => {
    setDraggedTeacher(teacher);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = async (e, dayOfWeek) => {
    e.preventDefault();
    if (!draggedTeacher) return;

    const dayDate = getDateForDay(dayOfWeek, selectedWeek);
    const dateStr = format(dayDate, 'yyyy-MM-dd');
    
    const existingSubstitute = getSubstitutesForDay(dayOfWeek);
    
    let teacherIds = [];
    if (existingSubstitute) {
      teacherIds = existingSubstitute.teachers_data.map(t => t.id);
    }
    
    // Add new teacher if not already present
    if (!teacherIds.includes(String(draggedTeacher.id))) {
      teacherIds.push(String(draggedTeacher.id));
    }

    try {
      if (existingSubstitute) {
        // Update existing
        await dispatch(updateDailySubstitute({
          id: existingSubstitute.id,
          data: {
            day_of_week: dayOfWeek,
            date: dateStr,
            teachers: teacherIds,
          }
        })).unwrap();
        showToast('تم تحديث المناوبين بنجاح', 'success');
      } else {
        // Create new
        await dispatch(createDailySubstitute({
          day_of_week: dayOfWeek,
          date: dateStr,
          teachers: teacherIds,
        })).unwrap();
        showToast('تم إضافة المناوبين بنجاح', 'success');
      }
    } catch (error) {
      showToast('حدث خطأ أثناء حفظ المناوبين', 'error');
    }

    setDraggedTeacher(null);
  };

  // Handle remove teacher from day
  const handleRemoveTeacher = async (dayOfWeek, teacherId) => {
    const existingSubstitute = getSubstitutesForDay(dayOfWeek);
    if (!existingSubstitute) return;

    const dayDate = getDateForDay(dayOfWeek, selectedWeek);
    const dateStr = format(dayDate, 'yyyy-MM-dd');
    
    const teacherIds = existingSubstitute.teachers_data
      .map(t => t.id)
      .filter(id => id !== String(teacherId));

    try {
      if (teacherIds.length === 0) {
        // Delete if no teachers left
        await dispatch(deleteDailySubstitute(existingSubstitute.id)).unwrap();
        showToast('تم حذف المناوبين بنجاح', 'success');
      } else {
        // Update
        await dispatch(updateDailySubstitute({
          id: existingSubstitute.id,
          data: {
            day_of_week: dayOfWeek,
            date: dateStr,
            teachers: teacherIds,
          }
        })).unwrap();
        showToast('تم تحديث المناوبين بنجاح', 'success');
      }
    } catch (error) {
      showToast('حدث خطأ أثناء تحديث المناوبين', 'error');
    }
  };

  // Handle move teacher between days
  const handleMoveTeacher = async (fromDay, toDay, teacherId) => {
    // Remove from source day
    await handleRemoveTeacher(fromDay, teacherId);
    
    // Add to target day
    const dayDate = getDateForDay(toDay, selectedWeek);
    const dateStr = format(dayDate, 'yyyy-MM-dd');
    const existingSubstitute = getSubstitutesForDay(toDay);
    
    let teacherIds = [];
    if (existingSubstitute) {
      teacherIds = existingSubstitute.teachers_data.map(t => t.id);
    }
    
    if (!teacherIds.includes(String(teacherId))) {
      teacherIds.push(String(teacherId));
    }

    try {
      if (existingSubstitute) {
        await dispatch(updateDailySubstitute({
          id: existingSubstitute.id,
          data: {
            day_of_week: toDay,
            date: dateStr,
            teachers: teacherIds,
          }
        })).unwrap();
      } else {
        await dispatch(createDailySubstitute({
          day_of_week: toDay,
          date: dateStr,
          teachers: teacherIds,
        })).unwrap();
      }
      showToast('تم نقل المعلم بنجاح', 'success');
    } catch (error) {
      showToast('حدث خطأ أثناء نقل المعلم', 'error');
    }
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };


  // Render day card
  const renderDayCard = (day) => {
    const daySubstitutes = getSubstitutesForDay(day.value);
    const teachers = daySubstitutes?.teachers_data || [];
    const absentIds = absentTeacherIdsByDay[day.value] || [];
    const dayDate = getDateForDay(day.value, selectedWeek);
    const dateStr = format(dayDate, 'yyyy-MM-dd');

    return (
      <MotionCard
        key={day.value}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        sx={{
          minHeight: 200,
          height: '100%',
          borderRadius: 3,
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          position: 'relative',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: '#4338CA',
            boxShadow: '0 4px 18px rgba(67,56,202,0.12)',
          },
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, day.value)}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#1E1B4B', fontFamily: FONT }}>
              {day.label}
            </Typography>
            <Chip
              label={dateStr}
              size="small"
              sx={{
                bgcolor: '#EEF2FF',
                color: '#4338CA',
                fontWeight: 700,
                fontSize: '0.72rem',
                fontFamily: FONT,
              }}
            />
          </Stack>

          <Box
            sx={{
              minHeight: 120,
              maxHeight: 400,
              p: 2,
              bgcolor: '#F8F9FF',
              borderRadius: 2.5,
              border: '2px dashed #E5E7EB',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {teachers.length === 0 ? (
              <Typography
                variant="body2"
                textAlign="center"
                sx={{ py: 4, color: '#6B7280', fontFamily: FONT }}
              >
                اسحب المعلمين هنا
              </Typography>
            ) : (
              <Stack spacing={1}>
                {teachers.map((teacher) => {
                  // Check if teacher is absent - compare IDs as strings
                  const teacherIdStr = String(teacher.id);
                  const isAbsent = absentIds.some(absentId => String(absentId) === teacherIdStr);
                  return (
                    <Chip
                      key={teacher.id}
                      label={teacher.name}
                      icon={<PersonIcon />}
                      onDelete={() => handleRemoveTeacher(day.value, teacher.id)}
                      draggable
                      onDragStart={(e) => {
                        const teacherObj = teachersList.find(t => String(t.id) === teacherIdStr);
                        if (teacherObj) {
                          handleDragStart(e, teacherObj);
                        }
                      }}
                      sx={{
                        bgcolor: isAbsent ? '#FEE2E2' : '#EEF2FF',
                        color: isAbsent ? '#DC2626' : '#4338CA',
                        fontWeight: isAbsent ? 800 : 600,
                        fontFamily: FONT,
                        borderRadius: 2,
                        '& .MuiChip-icon': { color: isAbsent ? '#DC2626' : '#4338CA' },
                        cursor: 'grab',
                        '&:active': {
                          cursor: 'grabbing',
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        </CardContent>
      </MotionCard>
    );
  };

  if (loading.fetch) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: '#F8F9FF' }}>
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
     <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Print styles */}
      <style>
        {`
          @media print {
            @page {
              margin: 1.5cm;
              size: A4;
            }
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            .print-list {
              display: block !important;
              visibility: visible !important;
            }
            .print-content .MuiCard,
            .print-content .MuiGrid-container {
              display: none !important;
            }
            .print-content .print-list {
              display: block !important;
              visibility: visible !important;
            }
            .print-content .print-list .MuiTableContainer-root {
              box-shadow: none !important;
            }
            .print-content .print-list table {
              width: 100%;
              border-collapse: collapse;
            }
            .print-content .print-list th,
            .print-content .print-list td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: right;
            }
            .print-content .print-list th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .print-content .print-list tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .print-content .print-list .MuiChip-root {
              display: inline-block;
              margin: 2px;
            }
          }
        `}
      </style>
      
      {/* Header */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="no-print"
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonIcon sx={{ color: '#fff', fontSize: 30 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
              المناوبون
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}
            >
              جدولة وإدارة المعلمين المناوبين أسبوعياً
            </Typography>
          </Box>
        </Stack>

        <Stack useFlexGap direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <IconButton
            onClick={() => {
              const newDate = new Date(selectedWeek);
              newDate.setDate(newDate.getDate() - 7);
              setSelectedWeek(newDate);
            }}
            sx={{
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<TodayIcon />}
            onClick={() => setSelectedWeek(new Date())}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              boxShadow: 'none',
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: FONT,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)', boxShadow: 'none' },
            }}
          >
            هذا الأسبوع
          </Button>
          <IconButton
            onClick={() => {
              const newDate = new Date(selectedWeek);
              newDate.setDate(newDate.getDate() + 7);
              setSelectedWeek(newDate);
            }}
            sx={{
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: FONT,
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            طباعة
          </Button>
        </Stack>
      </MotionPaper>

      <div className="print-content">
        {/* Print Header */}
        <Box sx={{ mb: 3, textAlign: 'center', display: { xs: 'none', print: 'block' } }} className="print-only">
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            جدول المناوبين
          </Typography>
          <Typography variant="h6" color="text.secondary">
            الأسبوع: {format(selectedWeek, 'yyyy-MM-dd')}
          </Typography>
        </Box>

        {/* Print List Format */}
        <Box className="print-list" sx={{ display: { xs: 'none', print: 'block' } }}>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                  <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', width: '20%', fontFamily: FONT }}>اليوم</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', width: '15%', fontFamily: FONT }}>التاريخ</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: FONT }}>المناوبون</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DAYS_OF_WEEK.map((day) => {
                  const daySubstitutes = getSubstitutesForDay(day.value);
                  const teachers = daySubstitutes?.teachers_data || [];
                  const absentIds = absentTeacherIdsByDay[day.value] || [];
                  const dayDate = getDateForDay(day.value, selectedWeek);
                  const dateStr = format(dayDate, 'yyyy-MM-dd');
                  
                  return (
                    <TableRow key={day.value} sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: FONT }}>{day.label}</TableCell>
                      <TableCell sx={{ color: '#6B7280', fontFamily: FONT }}>{dateStr}</TableCell>
                      <TableCell>
                        {teachers.length === 0 ? (
                          <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: FONT }}>
                            لا يوجد مناوبون
                          </Typography>
                        ) : (
                          <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
                            {teachers.map((teacher) => {
                              const teacherIdStr = String(teacher.id);
                              const isAbsent = absentIds.some(absentId => String(absentId) === teacherIdStr);
                              return (
                                <Chip
                                  key={teacher.id}
                                  label={teacher.name}
                                  size="small"
                                  sx={{
                                    bgcolor: isAbsent ? '#FEE2E2' : '#EEF2FF',
                                    color: isAbsent ? '#DC2626' : '#4338CA',
                                    fontWeight: isAbsent ? 800 : 600,
                                    fontFamily: FONT,
                                    margin: '2px',
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Teachers List - Draggable */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          }}
          className="no-print"
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: FONT }}
          >
            المعلمون المتاحون
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {teachersList.map((teacher) => (
              <Chip
                key={teacher.id}
                label={teacher.full_name}
                icon={<PersonIcon />}
                draggable
                onDragStart={(e) => handleDragStart(e, teacher)}
                sx={{
                  bgcolor: '#EEF2FF',
                  color: '#4338CA',
                  fontWeight: 600,
                  fontFamily: FONT,
                  borderRadius: 2,
                  '& .MuiChip-icon': { color: '#4338CA' },
                  cursor: 'grab',
                  transition: 'background-color 0.2s ease',
                  '&:hover': { bgcolor: '#E0E7FF' },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              />
            ))}
          </Stack>
        </MotionPaper>

        {/* Weekly Schedule - Cards (hidden when printing) */}
        <Grid container spacing={2} className="no-print">
          {DAYS_OF_WEEK.map((day) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={day.value}>
              {renderDayCard(day)}
            </Grid>
          ))}
        </Grid>
      </div>
     </Box>
    </Box>
  );
};

export default SubstituteTeachersPage;
